/* Ayudate · Motor de búsqueda y listado de tutores
   Extraído de SitioPublico.jsx (sección "buscador") para poder reutilizarlo
   tal cual dentro del panel de estudiante ("Solicitar nueva tutoría", ver
   DashboardEstudiante.jsx) sin duplicar el buscador entero. SitioPublico.jsx
   sigue siendo dueño de la carga de datos (api.getTutores()) y de todo el
   texto/maquetado propio de la página pública (eyebrow, títulos, párrafos):
   este componente solo es el buscador + la grilla de resultados + su
   paginación + el modal de Agenda, parametrizado por los tutores ya
   cargados. `perfilEstudiante` es opcional: si viene (panel de estudiante
   logueado), precarga nombre/correo/teléfono en el formulario de Agenda.
   `consulta` también lo es: es lo que escribió alguien en el buscador de la
   portada ({ texto, n }), para que este buscador arranque ya filtrado por
   eso en vez de tener dos motores de búsqueda distintos en la misma
   página. */

import React, { useState, useMemo, useEffect, useRef } from "react";
import { AREAS, INSTITUCIONES, infoCurso } from "../datos/areas";
import { CONTACTO } from "../datos/contacto";
import { tarifas } from "../lib/tarifas";
import { FRANJAS, expandirOfertas } from "../lib/horarios";
import { promedioGeneral, puntaje, masAntiguoPrimero } from "../lib/orden";
import { buscarCursosPublico, cursoCoincideBusqueda } from "../lib/buscar";
import { Agenda } from "./Agenda";
import { TutorCard } from "./TutorCard";
import { irA_ruta } from "../lib/rutas";

export function CatalogoTutores({ tutores, cargando, errorCarga, onReintentar, perfilEstudiante,
                                  consulta }) {
  const [institucion, setInstitucion] = useState("");
  const [area, setArea] = useState("todas");
  const [curso, setCurso] = useState("todos");
  const [franja, setFranja] = useState("todas");
  const [orden, setOrden] = useState("sesiones");
  const [abierto, setAbierto] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [focoBusqueda, setFocoBusqueda] = useState(-1);
  const [sugBusquedaAbierta, setSugBusquedaAbierta] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [cursosAbiertos, setCursosAbiertos] = useState(false);

  /* Una sola expansión de ofertas para toda la pantalla. expandirOfertas deja
     una fila por tutor-y-curso, así que contar filas por código ES contar
     tutores: nadie puede ofrecer dos veces el mismo curso. De ahí salen los
     dos contadores — por curso para los chips, y sumado por área para las
     tarjetas de área y de institución.

     Antes cada tarjeta llamaba a expandirOfertas por su cuenta; con la UCR y
     Software en la lista eso son ochenta expansiones del catálogo entero en
     cada render, para contar siempre lo mismo. */
  const ofertas = useMemo(() => expandirOfertas(tutores), [tutores]);
  const conteo = useMemo(() => {
    const porCurso = {}, porArea = {};
    ofertas.forEach((o) => {
      porCurso[o.curso] = (porCurso[o.curso] || 0) + 1;
      const a = infoCurso(o.curso).area;
      porArea[a] = (porArea[a] || 0) + 1;
    });
    return { porCurso, porArea };
  }, [ofertas]);
  const tutoresDe = (ids) => ids.reduce((n, a) => n + (conteo.porArea[a] || 0), 0);

  const cursosVisibles = area === "todas" ? [] : AREAS.find((a) => a.id === area).cursos;
  const institucionActual = INSTITUCIONES.find((i) => i.id === institucion);
  /* En el orden que declara la institución, no en el que caen dentro de AREAS:
     el arreglo `areas` de cada institución viene agrupado a propósito (en el
     Tec: básicas, computación, ingenierías, diseño…) y filtrar AREAS lo
     perdía, dejando las áreas en el orden en que están escritas en el
     archivo, que no es el que le sirve a nadie. */
  const areasVisibles = institucionActual
    ? institucionActual.areas.map((id) => AREAS.find((a) => a.id === id)).filter(Boolean)
    : [];

  /* Imperativo (no useEffect) para que el buscador por curso pueda fijar
     institución + área + curso a la vez sin que un efecto los pise.
     Volver a tocar la institución ya marcada la desmarca: vuelve a "" (sin
     institución elegida), igual que al cargar la página, mostrando de
     nuevo tutores de todas las áreas. */
  const elegirInstitucion = (id) => {
    setInstitucion((prev) => (prev === id ? "" : id));
    setArea("todas");
    setCurso("todos");
    setCursosAbiertos(false);
  };
  const elegirArea = (id) => { setArea(id); setCurso("todos"); setCursosAbiertos(false); };

  /* Las áreas de la UCR traen listas largas — Física pasa de 70 cursos — y
     volcarlas enteras dejaría los filtros y los resultados sepultados bajo
     una pared de chips. Se muestran las primeras y el resto
     queda tras un botón. Quien ya sabe qué curso busca no pasa por acá: lo
     escribe en el buscador de arriba y cae directo en él.

     Cuando hay que recortar, los cursos que YA tienen tutores pasan al
     frente. Sin eso el recorte escondía justamente lo único que le sirve a
     quien busca: en Matemática de la UCR, MA-1001 con su tutor caía en el
     puesto treinta y tantos por orden de código, detrás de una fila de
     cursos sin nadie, y el área se veía vacía teniéndolo. Las listas que
     caben enteras no se tocan: ahí el orden del catálogo manda. */
  const CHIPS_INICIALES = 24;
  const cursosMostrados = cursosAbiertos || cursosVisibles.length <= CHIPS_INICIALES
    ? cursosVisibles
    : [...cursosVisibles.filter((c) => conteo.porCurso[c.cod]),
       ...cursosVisibles.filter((c) => !conteo.porCurso[c.cod])].slice(0, CHIPS_INICIALES);

  /* Buscador libre por código o palabra clave (ver lib/buscar.js): al
     elegir una sugerencia saltamos directo a institución/área/curso, como
     si la persona hubiera hecho clic en cada nivel a mano. */
  const sugerenciasBusqueda = buscarCursosPublico(busqueda);
  const elegirCursoBusqueda = (c) => {
    const inst = INSTITUCIONES.find((i) => i.areas.includes(c.area)) || institucionActual;
    setInstitucion(inst.id);
    setArea(c.area);
    setCurso(c.cod);
    setCursosAbiertos(true);   // el curso elegido puede caer fuera de los primeros chips
    setBusqueda(""); setFocoBusqueda(-1); setSugBusquedaAbierta(false);
  };
  const teclasBusqueda = (e) => {
    if (!sugBusquedaAbierta || sugerenciasBusqueda.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocoBusqueda((f) => (f + 1) % sugerenciasBusqueda.length);
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocoBusqueda((f) => (f - 1 + sugerenciasBusqueda.length) % sugerenciasBusqueda.length);
    }
    if (e.key === "Enter") {
      e.preventDefault();
      elegirCursoBusqueda(sugerenciasBusqueda[focoBusqueda >= 0 ? focoBusqueda : 0]);
    }
    if (e.key === "Escape") setSugBusquedaAbierta(false);
  };

  const resultados = useMemo(() => {
    const r = ofertas.filter((t) => {
      if (franja !== "todas" && !t.franjas.includes(franja)) return false;
      if (busqueda.trim().length >= 2 && !cursoCoincideBusqueda(t.curso, busqueda)) return false;
      if (curso !== "todos") return t.curso === curso;
      if (area !== "todas") return infoCurso(t.curso).area === area;
      if (institucionActual) return institucionActual.areas.includes(infoCurso(t.curso).area);
      return true;
    });
    const C = promedioGeneral(tutores);
    /* El último desempate es siempre la antigüedad, para todos los órdenes:
       dos tarjetas que pesan exactamente lo mismo —mismas sesiones, misma
       calificación, mismo precio— salen con la más vieja adelante. Antes ese
       empate lo rompía el orden en que venían las ofertas, que es el de la
       hoja, y ahí la fila más NUEVA va arriba (se inserta al inicio): la
       tarjeta recién publicada le ganaba el puesto a la que llevaba meses
       igualada con ella, que es justo al revés. Ver masAntiguoPrimero en
       lib/orden.js. */
    return r.sort((a, b) => {
      const porNota = () => puntaje(b, C) - puntaje(a, C) || b.resenas - a.resenas
        || masAntiguoPrimero(a, b);
      if (orden === "precio-asc") return tarifas(a).cliente - tarifas(b).cliente || porNota();
      if (orden === "precio-desc") return tarifas(b).cliente - tarifas(a).cliente || porNota();
      return b.sesiones - a.sesiones || porNota();
    });
  }, [tutores, ofertas, institucion, area, curso, franja, orden, busqueda]);

  /* "Ver mi perfil público" del dashboard del tutor (ver DashboardTutor.jsx)
     manda acá con ?tutorId=... en vez de enganchar el deep-link a los
     filtros de institución/área/curso — reconstruir a cuál pertenece un
     curso puntual sería frágil, así que en su lugar se arma una vista
     dedicada con exactamente sus ofertas publicadas, tal como las vería
     cualquier estudiante (getTutores() ya filtra por publicado/aprobado,
     así que no hace falta repetir ese filtro acá). */
  const [tutorIdPreview] = useState(
    () => new URLSearchParams(window.location.search).get("tutorId") || "");
  const previaOfertas = useMemo(() => (
    tutorIdPreview ? ofertas.filter((t) => String(t.id) === String(tutorIdPreview)) : []
  ), [ofertas, tutorIdPreview]);

  const hayFiltros = area !== "todas" || curso !== "todos" || franja !== "todas" || busqueda.trim() !== "";
  const limpiarFiltros = () => {
    setArea("todas"); setCurso("todos"); setFranja("todas"); setBusqueda("");
  };

  /* ---------- Paginación: 9 tutores por página ---------- */
  const POR_PAGINA = 9;
  const paginas = Math.max(1, Math.ceil(resultados.length / POR_PAGINA));
  const pagSegura = Math.min(pagina, paginas);
  const visibles = resultados.slice((pagSegura - 1) * POR_PAGINA, pagSegura * POR_PAGINA);

  useEffect(() => { setPagina(1); }, [area, curso, franja, orden, busqueda]);

  /* Lo que llega del buscador de la portada entra como una búsqueda libre y
     de paso suelta institución, área y curso: quien escribe "cálculo"
     arriba está empezando de cero, y dejarle puesto el filtro de área que
     había tocado antes le escondería justo lo que buscó. La dependencia es
     el objeto entero —cambia en cada envío gracias a su contador— para que
     buscar dos veces la misma palabra vuelva a aplicarse.

     Las sugerencias se dejan cerradas a propósito: la lista viene de
     escribir en ESTE campo, y desplegarla sola al aterrizar taparía los
     resultados que la persona vino a ver. */
  useEffect(() => {
    if (!consulta) return;
    setBusqueda(consulta.texto);
    setInstitucion(""); setArea("todas"); setCurso("todos");
    setCursosAbiertos(false);
    setSugBusquedaAbierta(false); setFocoBusqueda(-1);
  }, [consulta]);

  const refResultados = useRef(null);
  const refOrdnota = useRef(null);
  const irPagina = (p) => {
    setPagina(p);
    (refOrdnota.current ?? refResultados.current)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    refResultados.current?.focus({ preventScroll: true });
  };

  return (
    <>
      <div className="bc" style={{ maxWidth: 460, marginBottom: 22 }}>
        <label htmlFor="busqueda-curso" className="lb">Buscar por código o palabra clave</label>
        <input id="busqueda-curso" className="in" value={busqueda} autoComplete="off"
          onChange={(e) => { setBusqueda(e.target.value); setFocoBusqueda(-1); setSugBusquedaAbierta(true); }}
          onFocus={() => setSugBusquedaAbierta(true)}
          onBlur={() => setTimeout(() => setSugBusquedaAbierta(false), 120)}
          onKeyDown={teclasBusqueda}
          placeholder="Ej: MA1102, Cálculo, Inglés…"
          aria-label="Buscar curso por código o palabra clave"
          aria-expanded={sugBusquedaAbierta && sugerenciasBusqueda.length > 0}
          aria-autocomplete="list" role="combobox" />
        {sugBusquedaAbierta && sugerenciasBusqueda.length > 0 && (
          <ul className="bcsug" role="listbox" aria-label="Cursos sugeridos">
            {sugerenciasBusqueda.map((c, i) => (
              <li key={c.cod}>
                <button type="button" role="option" aria-selected={i === focoBusqueda}
                  className={`bcop ${i === focoBusqueda ? "on" : ""}`}
                  onMouseEnter={() => setFocoBusqueda(i)}
                  onClick={() => elegirCursoBusqueda(c)}>
                  {!c.sinCodigo && <span className="bccod" style={{ "--ac": c.color }}>{c.cod}</span>}
                  <span className="bcnom">{c.nom}<i>{c.instNom ? `${c.instNom} · ${c.areaNom}` : c.areaNom}</i></span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="instituciones">
        {INSTITUCIONES.map((inst) => {
          const n = tutoresDe(inst.areas);
          return (
            <button key={inst.id} className="icard" aria-pressed={institucion === inst.id}
              style={{ "--ac": inst.color }} onClick={() => elegirInstitucion(inst.id)}>
              <span className="bar" /><span className="ilbl">{inst.nombre}</span>
              <span className="idesc">{inst.desc} · {n} oferta{n === 1 ? "" : "s"}</span>
            </button>
          );
        })}
      </div>

      {institucionActual && (
        <div className="areas">
          <button className="acard" aria-pressed={area === "todas"} style={{ "--ac": institucionActual.color }}
            onClick={() => elegirArea("todas")}>
            <span className="bar" /><span className="lbl">Todas</span>
            <span className="qty">
              {tutoresDe(institucionActual.areas)} ofertas
            </span>
          </button>
          {areasVisibles.map((a) => {
            const n = conteo.porArea[a.id] || 0;
            return (
              <button key={a.id} className="acard" aria-pressed={area === a.id}
                style={{ "--ac": a.color }} onClick={() => elegirArea(a.id)}>
                <span className="bar" />
                <span className="lbl">{a.nombre}</span>
                <span className="qty">
                  {a.cursos.length > 1
                    ? `${a.cursos.length} cursos · ${n} oferta${n === 1 ? "" : "s"}`
                    : `${n} oferta${n === 1 ? "" : "s"}`}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Un área de un solo curso no abre lista de cursos: el área ES el curso
          (Simulacros del examen de admisión, Python o AutoCAD en Software) y
          una fila con "Todos" y un único chip sería puro trámite. Tocar el
          área ya deja abajo a sus tutores. Solo las áreas con varios cursos
          —los de una carrera, o los tres niveles de Excel— piden elegir. */}
      {cursosVisibles.length > 1 && (
        <div className="cursos" style={{ "--ac": AREAS.find((a) => a.id === area).color }}>
          <button className="cchip" aria-pressed={curso === "todos"} onClick={() => setCurso("todos")}>
            Todos
          </button>
          {cursosMostrados.map((c) => {
            /* Cuántos tutores hay ya en ese curso. Los que no tienen a nadie no
               muestran nada: un "(0)" colgando de cada curso vacío sería puro
               ruido en un área de doscientos. El aria-label dice qué es ese
               número, que entre paréntesis no se explica solo. */
            const n = conteo.porCurso[c.cod] || 0;
            const nombre = c.sinCodigo ? c.nom : `${c.cod} · ${c.nom}`;
            return (
              <button key={c.cod} className="cchip" aria-pressed={curso === c.cod}
                aria-label={n > 0 ? `${nombre}, ${n} tutor${n === 1 ? "" : "es"}` : undefined}
                onClick={() => setCurso(c.cod)}>
                {nombre}{n > 0 && <span className="cnum"> ({n})</span>}
              </button>
            );
          })}
          {cursosVisibles.length > CHIPS_INICIALES && (
            <button className="cchip cmas" onClick={() => setCursosAbiertos((v) => !v)}>
              {cursosAbiertos
                ? "Ver menos"
                : (() => {
                    const faltan = cursosVisibles.length - cursosMostrados.length;
                    return faltan === 1 ? "Ver el curso restante" : `Ver los ${faltan} cursos restantes`;
                  })()}
            </button>
          )}
        </div>
      )}

      <div className="ctrl">
        <div className="fld">
          <label htmlFor="f">Disponibilidad</label>
          <select id="f" value={franja} onChange={(e) => setFranja(e.target.value)}>
            <option value="todas">Cualquiera</option>
            {FRANJAS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </div>
        <div className="fld">
          <label htmlFor="o">Ordenar por</label>
          <select id="o" value={orden} onChange={(e) => setOrden(e.target.value)}>
            <option value="sesiones">Más sesiones</option>
            <option value="precio-asc">Precio · de menor a mayor</option>
            <option value="precio-desc">Precio · de mayor a menor</option>
          </select>
        </div>
      </div>

      <p className="ordnota" ref={refOrdnota}>
        {orden.startsWith("precio")
          ? "Ordenado por lo que paga el estudiante."
          : "Ordenado por cantidad de sesiones dadas."}
        {" "}En cualquier empate manda la calificación, que combina la nota con la cantidad de
        reseñas: un <b>4,9</b> con muchas opiniones pesa más que un <b>5,0</b> con dos o tres.
      </p>
      <div className="count" role="status" aria-live="polite">
        {resultados.length > POR_PAGINA
          ? `${(pagSegura - 1) * POR_PAGINA + 1}–${Math.min(pagSegura * POR_PAGINA, resultados.length)} de ${resultados.length} ofertas`
          : `${resultados.length} oferta${resultados.length === 1 ? "" : "s"}`}
        {curso !== "todos" ? ` · ${curso}` : area !== "todas" ? ` · ${AREAS.find((a) => a.id === area).nombre}` : ""}
      </div>

      <div className="container" ref={refResultados} tabIndex={-1}>
      {tutorIdPreview ? (
        <>
          <div className="empty">
            <div className="anton" style={{ fontSize: 24, marginBottom: 10 }}>
              Así ve un estudiante su perfil
            </div>
            <p className="sub" style={{ margin: "0 auto 18px" }}>
              {previaOfertas.length
                ? "Esta es la vista pública real de sus cursos publicados."
                : "Ninguno de sus cursos está publicado ahora mismo, así que no aparece en el buscador."}
            </p>
            <button className="btn" onClick={() => irA_ruta("/")}>Ver el buscador completo</button>
          </div>
          {previaOfertas.length > 0 && (
            <div className="grid">
              {previaOfertas.map((t) => (
                <TutorCard key={t.ofertaId} t={t}
                  activo={abierto?.ofertaId === t.ofertaId}
                  onVerHorarios={setAbierto} />
              ))}
            </div>
          )}
        </>
      ) : cargando ? (
        <div className="grid" aria-busy="true" aria-live="polite">
          {Array.from({ length: 6 }, (_, i) => (
            <div className="esq" key={i}>
              <div className="esq-top">
                <span className="esq-av" />
                <span><i style={{ width: 118 }} /><i style={{ width: 84 }} /></span>
              </div>
              <i style={{ width: "72%" }} /><i style={{ width: "58%" }} />
              <i style={{ width: "90%", height: 30, marginTop: 8 }} />
              <div className="esq-pr"><i style={{ width: 90, height: 22 }} />
                <i style={{ width: 96, height: 34, borderRadius: 99 }} /></div>
            </div>
          ))}
        </div>
      ) : errorCarga ? (
        <div className="empty">
          <div className="anton" style={{ fontSize: 24, marginBottom: 10 }}>
            No se pudo cargar
          </div>
          <p className="sub" style={{ margin: "0 auto 18px" }}>{errorCarga}</p>
          <button className="btn btn-p" onClick={onReintentar}>Reintentar</button>
        </div>
      ) : resultados.length === 0 ? (
        <div className="empty">
          <div className="anton" style={{ fontSize: 28, marginBottom: 10 }}>
            Ninguna oferta con esos filtros
          </div>
          <p className="sub" style={{ margin: "0 auto 18px" }}>
            {hayFiltros
              ? "Pruebe quitando algún filtro, o escríbanos y le ayudamos a buscar."
              : "Todavía no hay tutores publicados. Escríbanos y le avisamos apenas aparezca uno."}
          </p>
          <div className="vacio-acc">
            {hayFiltros && (
              <button className="btn" onClick={limpiarFiltros}>Quitar todos los filtros</button>
            )}
            <a className="btn btn-p" href={`https://wa.me/${CONTACTO.whatsapp}`}
              target="_blank" rel="noopener noreferrer">
              Escríbanos por WhatsApp
            </a>
          </div>
        </div>
      ) : (
        <div className="grid">
          {visibles.map((t) => (
            <TutorCard key={t.ofertaId} t={t}
              activo={abierto?.ofertaId === t.ofertaId}
              onVerHorarios={setAbierto} />
          ))}
        </div>
      )}

      {paginas > 1 && (
        <nav className="pag" aria-label="Páginas de resultados">
          <button className="pbtn" disabled={pagSegura === 1}
            onClick={() => irPagina(pagSegura - 1)} aria-label="Página anterior">‹</button>

          {Array.from({ length: paginas }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === paginas || Math.abs(p - pagSegura) <= 1)
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) => p === "…"
              ? <span className="psalto" key={`s${i}`} aria-hidden="true">…</span>
              : (
                <button key={p} className={`pbtn ${p === pagSegura ? "on" : ""}`}
                  aria-current={p === pagSegura ? "page" : undefined}
                  aria-label={`Página ${p}`} onClick={() => irPagina(p)}>{p}</button>
              ))}

          <button className="pbtn" disabled={pagSegura === paginas}
            onClick={() => irPagina(pagSegura + 1)} aria-label="Página siguiente">›</button>
        </nav>
      )}
      </div>

      {abierto && (
        <Agenda tutor={abierto} onClose={() => setAbierto(null)} perfilInicial={perfilEstudiante} />
      )}
    </>
  );
}
